import { branding } from './config';
import { username } from './auth';
import { CIE10_ODONTOLOGIA } from './cie10-odontologia';
import { classifySpecialty, createEncounter, loadPrescriber, savePrescriber, searchCie10, searchCie11, submitNlpFeedback } from './clinic-api';
import { emptyHistoria, emptyReceta, type Historia033, type RecetaAcess } from './print-docs';
import { resolveAssignedSpecialty, specialtyLabel, specialtyOption } from './specialty-picker';

export async function renderConsulta(
  stage: HTMLElement,
  clinicName: string,
  onSaved: (id: number) => void,
  specialty = 'odontologia',
  assignedSpecialties: string[] = [specialty]
): Promise<void> {
  const prescriber = await loadPrescriber().catch(() => ({
    nombre: username(),
    registroAcess: '',
    telefono: '',
    hasFirma: false,
  }));
  const availableSpecialties = [...new Set((assignedSpecialties.length ? assignedSpecialties : [specialty]).filter(Boolean))];
  const initialSpecialty = availableSpecialties.includes(specialty)
    ? specialty
    : availableSpecialties[0] || specialty;
  stage.innerHTML = `
    <div class="workspace">
      <div class="dash-head">
        <p class="eyebrow">ACESS-2023-0030 · HCU 033</p>
        <h1>Registrar consulta</h1>
        <p>Pega o escribe el dictado. Completa los campos de receta e historia. La firma debe ser el nombre completo, no una rúbrica.</p>
      </div>
      <p class="err" id="cerr" hidden></p>
      <form id="consulta" class="clinic-form">
        <section class="card">
          <p class="kicker">Dictado</p>
          <label>Texto clínico</label>
          <textarea name="dictado" rows="5" placeholder="Nombre, edad, alergias, motivo, CIE, medicamento DCI, dosis…"></textarea>
          <button class="btn quiet" type="button" id="map">Analizar dictado y completar campos sugeridos</button>
          <small class="muted">Revisa y confirma los campos antes de guardar.</small>
        </section>
        <section class="card">
          <p class="kicker">Profesional</p>
          <div class="row2">
            <div><label>Apellidos y nombres</label><input name="prescriptor" required value="${esc(prescriber.nombre)}" /></div>
            <input type="hidden" name="registro" value="NA" />
          </div>
          <label>Teléfono de contacto</label>
          <input name="telefono" value="${esc(prescriber.telefono)}" />
          <label>Firma manuscrita (nombre completo)</label>
          <canvas id="firma" width="520" height="140"></canvas>
          <button class="btn quiet" type="button" id="clear-firma">Borrar firma</button>
        </section>
        <section class="card">
          <p class="kicker">Paciente y diagnóstico (receta art. 5.b + historia)</p>
          <label for="specialty-search">Especialidad de esta atención</label>
          ${availableSpecialties.length > 1
            ? `<input id="specialty-search" type="search" list="specialty-options" autocomplete="off" aria-describedby="specialty-status specialty-classifier-status" placeholder="Buscar entre las especialidades asignadas" value="${esc(specialtyOption(initialSpecialty))}" />
              <datalist id="specialty-options">${availableSpecialties.map((code) => `<option value="${esc(specialtyOption(code))}"></option>`).join('')}</datalist>
              <input type="hidden" name="cie-specialty" value="${esc(initialSpecialty)}" />
              <small id="specialty-status" class="muted" role="status" aria-live="polite">Puedes buscar entre las especialidades asignadas a tu clínica.</small>`
            : `<input type="hidden" name="cie-specialty" value="${esc(initialSpecialty)}" /><p class="muted">${esc(specialtyLabel(initialSpecialty))} · única especialidad asignada a tu clínica</p>`}
          <div id="specialty-classifier-status" class="specialty-assist" hidden role="status" aria-live="polite" aria-atomic="true"></div>
          <div class="row2">
            <div><label>Apellidos y nombres</label><input name="paciente" required /></div>
            <div><label>Edad</label><input name="edad" required placeholder="40 años / 3 años 2 meses" /></div>
          </div>
          <div class="row2">
            <div><label>Cédula</label><input name="cedula" /></div>
            <div><label>Código diagnóstico CIE-10 / CIE-11</label>
              <input name="cie" required list="cies" autocomplete="off" value="${esc(new URLSearchParams(location.search).get('cie') || '')}" placeholder="Busca en ambas clasificaciones" />
              <datalist id="cies">${staticCieOptionsFor(initialSpecialty)}</datalist>
            </div>
          </div>
          <div id="cie-training-suggestions" class="muted" aria-live="polite"></div>
          <label>Alergias</label>
          <input name="alergias" required value="NINGUNA CONOCIDA" />
          <label>Motivo de consulta</label>
          <input name="motivo" required />
          <label>Frase del diagnóstico tal como aparece en el dictado (para mejorar sugerencias)</label>
          <input name="diagnosis-phrase" placeholder="Ej.: caries de dentina" />
          <label>Enfermedad actual / examen</label>
          <textarea name="enfermedad" rows="3"></textarea>
        </section>
        <section class="card">
          <p class="kicker">Medicamento (DCI en primer lugar)</p>
          <div class="row2">
            <div><label>DCI / genérico</label><input name="dci" required /></div>
            <div><label>Forma farmacéutica</label><input name="forma" required placeholder="cápsula, suspensión…" /></div>
          </div>
          <div class="row2">
            <div><label>Concentración</label><input name="conc" required /></div>
            <div><label>Vía</label><input name="via" required value="oral" /></div>
          </div>
          <div class="row2">
            <div><label>Cantidad (número)</label><input name="cantN" required /></div>
            <div><label>Cantidad (letras)</label><input name="cantL" required /></div>
          </div>
          <div class="row2">
            <div><label>Dosis</label><input name="dosis" required /></div>
            <div><label>Frecuencia</label><input name="frec" required placeholder="cada 8 horas" /></div>
          </div>
          <label>Duración</label>
          <input name="dur" required placeholder="7 días" />
          <label>Indicaciones al paciente</label>
          <textarea name="indicaciones" rows="2"></textarea>
          <label>Signos de alarma</label>
          <input name="alarma" />
          <label>Recomendaciones no farmacológicas</label>
          <input name="nofarma" />
        </section>
        <section class="card">
          <p class="kicker">Mejorar el dictado</p>
          <p>Si corregiste campos, puedes enviar este ejemplo para mejorar la extracción y las sugerencias de esta especialidad. El servicio elimina nombres de paciente antes de guardarlo; Admin Vyecom permitirá incluirlo en el siguiente entrenamiento.</p>
          <label>Frase del diagnóstico tal como se dictó
            <input name="diagnosis-phrase" placeholder="Ej.: caries de dentina en pieza 36" />
          </label>
          <small class="muted">Copia la frase exacta que aparece en el dictado; se usará para asociar el código CIE elegido con esa expresión.</small>
          <label><input id="training-consent" type="checkbox" /> Acepto usar este dictado corregido para mejorar el sistema.</label>
          <button class="btn quiet" id="send-training" type="button">Enviar corrección</button>
          <p id="training-status" class="muted" role="status" aria-live="polite"></p>
        </section>
        <button class="btn primary" type="submit">Guardar receta e historia y abrir PDF</button>
      </form>
    </div>`;

  const canvas = stage.querySelector('#firma') as HTMLCanvasElement;
  const strokes = attachSignature(canvas);
  stage.querySelector('#clear-firma')?.addEventListener('click', () => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokes.count = 0;
  });
  const form = stage.querySelector<HTMLFormElement>('#consulta')!;
  stage.querySelector('#send-training')?.addEventListener('click', async () => {
    const status = stage.querySelector<HTMLElement>('#training-status');
    const consent = stage.querySelector<HTMLInputElement>('#training-consent');
    const button = stage.querySelector<HTMLButtonElement>('#send-training');
    try {
      if (stage.querySelector<HTMLInputElement>('#specialty-search')?.dataset.valid === 'false') {
        throw new Error('Elige una especialidad asignada de la lista.');
      }
      if (!consent?.checked) throw new Error('Marca la casilla para enviar este dictado al entrenamiento.');
      const data = new FormData(form);
      const text = String(data.get('dictado') || '').trim();
      if (text.length < 10) throw new Error('Escribe primero el dictado que quieres corregir.');
      const selectedProfile = selectedSpecialty(form, specialty);
      const diagnosis = String(data.get('cie') || '').trim();
      const normalizedDiagnosis = diagnosis.replace(/^CIE-(?:10|11)\s*/i, '').trim();
      const cieCode = normalizedDiagnosis.match(/\b([A-TV-Z]\d{2}(?:\.\d{1,2})?|[A-Z0-9]{3,7})\b/i)?.[1];
      if (button) button.disabled = true;
      await submitNlpFeedback({
        text,
        predicted: {},
        corrected: { structured: {
          paciente: String(data.get('paciente') || ''),
          diagnostico: normalizedDiagnosis,
          motivo: String(data.get('motivo') || ''),
          enfermedad: String(data.get('enfermedad') || ''),
          alergias: String(data.get('alergias') || ''),
          medicamentos: [{ nombre: String(data.get('dci') || ''), dosis: String(data.get('dosis') || ''),
            via: String(data.get('via') || ''), frecuencia: String(data.get('frec') || ''),
            duracion: String(data.get('dur') || ''), cantidad: String(data.get('cantN') || '') }],
        } },
        consentTraining: true,
        specialty: selectedProfile,
        diagnosisPhrase: String(data.get('diagnosis-phrase') || '').trim(),
        ...(diagnosis.match(/^CIE-11\s/i) ? { cie11Code: cieCode } : { cie10Code: cieCode }),
      });
      if (status) status.textContent = 'Corrección enviada a la cola de entrenamiento de esta clínica y especialidad.';
      if (consent) consent.checked = false;
    } catch (error) {
      if (status) status.textContent = error instanceof Error ? error.message : String(error);
    } finally {
      if (button) button.disabled = false;
    }
  });
  stage.querySelector<HTMLButtonElement>('#map')?.addEventListener('click', (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    button.disabled = true;
    button.textContent = 'Analizando…';
    void mapDictado(stage, selectedSpecialty(form, specialty), availableSpecialties)
      .finally(() => {
        if (button.isConnected) {
          button.disabled = false;
          button.textContent = 'Analizar dictado y completar campos sugeridos';
        }
      });
  });
  let cieTimer: ReturnType<typeof setTimeout> | undefined;
  const updateCieSuggestions = (): void => {
    if (cieTimer) clearTimeout(cieTimer);
    cieTimer = setTimeout(() => void loadCieSuggestions(form, selectedSpecialty(form, specialty)), 300);
  };
  const specialtySearch = stage.querySelector<HTMLInputElement>('#specialty-search');
  if (specialtySearch) specialtySearch.dataset.valid = 'true';
  specialtySearch?.addEventListener('input', () => {
    const match = resolveAssignedSpecialty(specialtySearch.value, availableSpecialties);
    const status = stage.querySelector<HTMLElement>('#specialty-status');
    if (!match) {
      specialtySearch.dataset.valid = 'false';
      if (status) status.textContent = 'Elige una opción de la lista; solo se permiten especialidades asignadas a tu clínica.';
      clearSpecialtySuggestion(stage);
      return;
    }
    (form.elements.namedItem('cie-specialty') as HTMLInputElement).value = match;
    specialtySearch.dataset.valid = 'true';
    if (status) status.textContent = `Usando ${specialtyLabel(match)} para sugerencias y correcciones de entrenamiento.`;
    clearSpecialtySuggestion(stage);
    const list = form.querySelector<HTMLDataListElement>('#cies');
    if (list) list.innerHTML = staticCieOptionsFor(match);
    updateCieSuggestions();
  });
  (form.elements.namedItem('dictado') as HTMLTextAreaElement).addEventListener('input', () => {
    clearSpecialtySuggestion(stage);
    updateCieSuggestions();
  });
  (form.elements.namedItem('motivo') as HTMLInputElement).addEventListener('input', updateCieSuggestions);
  (form.elements.namedItem('cie-specialty') as HTMLSelectElement | HTMLInputElement)?.addEventListener('change', () => {
    const list = form.querySelector<HTMLDataListElement>('#cies');
    if (list) list.innerHTML = staticCieOptionsFor(selectedSpecialty(form, specialty));
    updateCieSuggestions();
  });
  stage.querySelector('#cie-training-suggestions')?.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-cie-code]');
    if (!button) return;
    const system = button.dataset.cieSystem || 'CIE-10';
    (form.elements.namedItem('cie') as HTMLInputElement).value = `${system} ${button.dataset.cieCode} ${button.dataset.cieDescription}`;
  });
  stage.querySelector('#consulta')?.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const err = stage.querySelector('#cerr') as HTMLElement;
    err.hidden = true;
    try {
      if (specialtySearch?.dataset.valid === 'false') throw new Error('Elige una especialidad asignada de la lista.');
      if (strokes.count < 40) {
        throw new Error('La firma es demasiado corta. Escribe el nombre completo (no una rúbrica).');
      }
      const form = ev.target as HTMLFormElement;
      const data = new FormData(form);
      const firma = canvas.toDataURL('image/png');
      const receta = buildReceta(clinicName, data);
      const historia = buildHistoria(clinicName, data, receta);
      await savePrescriber({
        nombre: String(data.get('prescriptor') || ''),
        registroAcess: String(data.get('registro') || ''),
        telefono: String(data.get('telefono') || ''),
        firmaPngBase64: firma,
      });
      const saved = await createEncounter({
        dictadoRaw: String(data.get('dictado') || ''),
        prescriptorNombre: String(data.get('prescriptor') || ''),
        registroAcess: String(data.get('registro') || ''),
        telefonoPrescriptor: String(data.get('telefono') || ''),
        firmaPngBase64: firma,
        receta,
        historia,
      });
      onSaved(saved.id);
    } catch (e) {
      err.hidden = false;
      err.textContent = e instanceof Error ? e.message : String(e);
    }
  });
}

async function loadCieSuggestions(form: HTMLFormElement, specialty: string): Promise<void> {
  const panel = form.closest('.workspace')?.querySelector<HTMLDivElement>('#cie-training-suggestions');
  if (!panel) return;
  const query = String(new FormData(form).get('motivo') || new FormData(form).get('dictado') || '').trim();
  if (query.length < 3) { panel.innerHTML = ''; return; }
  try {
    const [cie10, cie11] = await Promise.all([searchCie10(query, specialty), searchCie11(query, specialty)]);
    const section = (system: 'CIE-10' | 'CIE-11', response: typeof cie10 | Awaited<ReturnType<typeof searchCie11>>): string => {
      if (!response.available) return `<section><strong>${system}</strong><p>Sugerencias no disponibles en este entorno.</p></section>`;
      const rows = response.results || [];
      const training = system === 'CIE-10' ? cie10.readiness?.cie10_training_only : cie11.readiness?.cie11_training_only;
      const label = training ? `Candidatos ${system} para entrenamiento:` : `Candidatos ${system}:`;
      return `<section><strong>${label}</strong>${rows.length ? rows.map((row) => `<button type="button" data-cie-system="${system}" data-cie-code="${esc(row.code)}" data-cie-description="${esc(row.description)}" class="btn quiet">${esc(row.code)} · ${esc(row.description)}</button>`).join('') : '<p>No se encontraron coincidencias.</p>'}</section>`;
    };
    panel.innerHTML = `${section('CIE-10', cie10)}${section('CIE-11', cie11)}<p>Seleccionar una sugerencia copia su código al diagnóstico.</p>`;
  } catch {
    panel.textContent = 'No se pudo consultar el catálogo CIE-10 en este momento.';
  }
}

function buildReceta(clinic: string, data: FormData): RecetaAcess {
  const dci = String(data.get('dci') || '');
  const dosis = String(data.get('dosis') || '');
  const frec = String(data.get('frec') || '');
  const via = String(data.get('via') || '');
  const dur = String(data.get('dur') || '');
  const rx = emptyReceta(clinic, branding.city);
  rx.pacienteApellidosNombres = String(data.get('paciente') || '');
  rx.edad = String(data.get('edad') || '');
  rx.diagnosticoCie = String(data.get('cie') || '');
  rx.alergias = String(data.get('alergias') || '');
  rx.indicaciones = String(data.get('indicaciones') || `${dci}, ${dosis}, ${frec}, vía ${via}, ${dur}`);
  rx.signosAlarma = String(data.get('alarma') || '');
  rx.recomendacionesNoFarmacologicas = String(data.get('nofarma') || '');
  rx.medicamentos = [
    {
      dci,
      formaFarmaceutica: String(data.get('forma') || ''),
      concentracion: String(data.get('conc') || ''),
      via,
      cantidadNumero: String(data.get('cantN') || ''),
      cantidadLetras: String(data.get('cantL') || ''),
      dosis,
      frecuencia: frec,
      duracion: dur,
    },
  ];
  return rx;
}

function buildHistoria(clinic: string, data: FormData, receta: RecetaAcess): Historia033 {
  const h = emptyHistoria(clinic);
  h.apellidosNombres = receta.pacienteApellidosNombres;
  h.cedula = String(data.get('cedula') || '');
  h.edad = receta.edad;
  h.alergias = receta.alergias;
  h.motivoConsulta = String(data.get('motivo') || '');
  h.enfermedadActual = String(data.get('enfermedad') || data.get('dictado') || '');
  h.diagnosticos = [{ cie: receta.diagnosticoCie, descripcion: receta.diagnosticoCie, preDef: 'DEF' }];
  h.prescripciones = receta.medicamentos
    .map((m) => `${m.dci} ${m.concentracion} ${m.formaFarmaceutica} ${m.dosis} ${m.frecuencia} ${m.duracion}`)
    .join('; ');
  return h;
}

async function mapDictado(root: Element, specialty: string, assignedSpecialties: string[] = [specialty]): Promise<void> {
  const form = root.querySelector('#consulta') as HTMLFormElement;
  const text = String(new FormData(form).get('dictado') || '');
  const classifierStatus = root.querySelector<HTMLElement>('#specialty-classifier-status');
  const set = (name: string, value: string, overwrite = false) => {
    const el = form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement | null;
    if (!el || (!overwrite && el.value.trim())) return;
    el.value = value;
  };
  // Complete the local draft immediately; the specialty request must not delay
  // field suggestions or apply against a transcript that has since changed.
  const dentalSpecialty = new Set(['odontologia', 'odontologia-general', 'ortodoncia', 'endodoncia']).has(specialty);
  const cie = dentalSpecialty ? CIE10_ODONTOLOGIA.find((c) => text.toUpperCase().includes(c.code.replace('.', '')) || text.toUpperCase().includes(c.code))
    || CIE10_ODONTOLOGIA.find((c) => text.toLowerCase().includes(c.label.split(' ')[0].toLowerCase())) : undefined;
  if (cie) set('cie', `${cie.code} ${cie.label}`);
  if (/ninguna/i.test(text)) set('alergias', 'NINGUNA CONOCIDA', true);
  const age = text.match(/(\d+\s*años(?:\s+\d+\s*meses)?)/i);
  if (age) set('edad', age[1]);
  const who = text.match(/paciente\s+([A-Za-zÁÉÍÓÚÑáéíóúñ]+(?:\s+[A-Za-zÁÉÍÓÚÑáéíóúñ]+){1,3})/i);
  if (who) set('paciente', who[1].toUpperCase());
  const motivo = text.match(/motivo[:\s]+([^.;]+)/i);
  if (motivo) set('motivo', motivo[1].trim());
  const dci = text.match(/\b(amoxicilina|ibuprofeno|paracetamol|metronidazol|clindamicina|nimesulida|ketorolaco|azitro?micina)\b/i);
  if (dci) set('dci', dci[1].toUpperCase());

  if (!classifierStatus) return;
  if (assignedSpecialties.length < 2) {
    clearSpecialtySuggestion(root);
    return;
  }
  classifierStatus.hidden = false;
  classifierStatus.dataset.state = 'loading';
  classifierStatus.textContent = text.trim().length < 8
    ? 'Dicta una frase clínica más completa para evaluar la especialidad. También puedes buscarla manualmente.'
    : 'Evaluando el texto para sugerir una especialidad asignada. La selección actual no cambiará sin tu confirmación.';
  if (text.trim().length < 8) return;
  if (assignedSpecialties.length > 1 && text.trim().length >= 8 && classifierStatus) {
    try {
      const classification = await classifySpecialty(text, assignedSpecialties);
      const currentText = String(new FormData(form).get('dictado') || '');
      if (currentText !== text || selectedSpecialty(form, specialty) !== specialty) return;
      classifierStatus.replaceChildren();
      if (classification.abstained || !classification.suggestion) {
        classifierStatus.dataset.state = 'uncertain';
        classifierStatus.textContent = 'No hay evidencia suficiente para sugerir una especialidad. Elige o busca una manualmente; el contexto actual no se modificó.';
      } else {
        classifierStatus.dataset.state = 'suggestion';
        const evidence = classification.candidates.find((candidate) => candidate.specialty === classification.suggestion)?.matched_terms || [];
        const title = document.createElement('strong');
        title.textContent = `Sugerencia: ${specialtyLabel(classification.suggestion)}`;
        const explanation = document.createElement('span');
        explanation.textContent = evidence.length
          ? `Detectada por estas expresiones: ${evidence.join(', ')}.`
          : 'El texto coincide con este perfil.';
        classifierStatus.append(title, explanation);
        const accept = document.createElement('button');
        accept.type = 'button'; accept.className = 'btn quiet'; accept.textContent = 'Usar especialidad sugerida';
        accept.addEventListener('click', () => {
          const picker = root.querySelector<HTMLInputElement>('#specialty-search');
          const hidden = form.elements.namedItem('cie-specialty') as HTMLInputElement;
          if (picker) {
            picker.value = specialtyOption(classification.suggestion!);
            picker.dataset.valid = 'true';
          }
          hidden.value = classification.suggestion!;
          const cieList = form.querySelector<HTMLDataListElement>('#cies');
          if (cieList) cieList.innerHTML = staticCieOptionsFor(classification.suggestion!);
          classifierStatus.replaceChildren();
          classifierStatus.dataset.state = 'accepted';
          classifierStatus.textContent = `Especialidad de esta atención: ${specialtyLabel(classification.suggestion!)}. Confirma que sea correcta.`;
          void loadCieSuggestions(form, classification.suggestion!);
        });
        classifierStatus.append(accept);
      }
    } catch {
      classifierStatus.dataset.state = 'unavailable';
      classifierStatus.textContent = 'No se pudo evaluar la especialidad. Puedes seleccionarla manualmente; el contexto no se modificó.';
    }
  }
}

function clearSpecialtySuggestion(root: Element): void {
  const panel = root.querySelector<HTMLElement>('#specialty-classifier-status');
  if (!panel) return;
  panel.replaceChildren();
  panel.hidden = true;
  delete panel.dataset.state;
}

function selectedSpecialty(form: HTMLFormElement, fallback: string): string {
  return String(new FormData(form).get('cie-specialty') || fallback);
}

function staticCieOptionsFor(specialty: string): string {
  const dental = new Set(['odontologia', 'odontologia-general', 'ortodoncia', 'endodoncia']).has(specialty);
  return dental ? CIE10_ODONTOLOGIA.map((c) => `<option value="${esc(c.code)} ${esc(c.label)}">`).join('') : '';
}

function attachSignature(canvas: HTMLCanvasElement): { count: number } {
  const ctx = canvas.getContext('2d');
  const state = { count: 0, drawing: false };
  if (!ctx) return state;
  ctx.strokeStyle = '#12263a';
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const pos = (ev: PointerEvent) => {
    const r = canvas.getBoundingClientRect();
    return { x: (ev.clientX - r.left) * (canvas.width / r.width), y: (ev.clientY - r.top) * (canvas.height / r.height) };
  };
  canvas.addEventListener('pointerdown', (ev) => {
    state.drawing = true;
    const p = pos(ev);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    canvas.setPointerCapture(ev.pointerId);
  });
  canvas.addEventListener('pointermove', (ev) => {
    if (!state.drawing) return;
    const p = pos(ev);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    state.count += 1;
  });
  canvas.addEventListener('pointerup', () => { state.drawing = false; });
  return state;
}

function esc(value: string): string {
  return String(value || '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]!));
}
